import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { errorMessage, isTransientError } from '@/lib/api';

const AUTO_RETRY_MS = 3000;

/**
 * Shown instead of a blank page when a route fails to load. Server hiccups (the API restarting,
 * a deploy, a proxy 502) retry on their own; anything else offers "Try again".
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const transient = isTransientError(error);
  const retry = () => {
    reset();
    void router.invalidate();
  };

  useEffect(() => {
    if (!transient) return;
    const timer = setTimeout(retry, AUTO_RETRY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- schedule once per failure
  }, [error]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md" role="alert">
        <CardHeader>
          <CardTitle>{transient ? 'Can’t reach the server' : 'Something went wrong'}</CardTitle>
          <CardDescription>
            {transient
              ? 'The API is not responding (it may be restarting). Retrying automatically…'
              : errorMessage(error)}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button onClick={retry}>Try again</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
